/**
 * Admin → Stamps (custom stamps, toiyp Plus).
 *
 * Stamp books (collections) and their designs: create a book, upload stamps
 * as they are, or upload a whole printed sheet and have it cut into single
 * stamps here in the browser (`sheetCutter.ts`). Every upload goes through a
 * review grid first — untick the bad cuts, name the good ones — and only then
 * is saved. Designs are retired, never deleted, so a letter already sent on
 * one keeps its stamp.
 *
 * Grants give any book to any member, outside the monthly gift limit. Only
 * admin grants are listed: member-to-member gifts would say who writes to whom.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { FileText, Mail, Scissors, Upload } from 'lucide-react';
import { PostageStamp } from '../postage/Stamp';
import {
  useAddStampDesignsMutation,
  useAdminStampsQuery,
  useCreateStampCollectionMutation,
  useGrantStampCollectionMutation,
  useRevokeStampGrantMutation,
  useUpdateStampCollectionMutation,
  useUpdateStampDesignMutation,
  type AdminStampCollection,
  type AdminUserRow,
} from './api';
import { cutSheet, type Pixels } from './sheetCutter';
import { PaperEditor, type PaperDraft } from './PaperEditor';
import { EnvelopeEditor, type EnvelopeDraft } from './EnvelopeEditor';
import { EnvelopeThumb } from '../stationery/Envelope';
import { detectWritingArea } from '../stationery/writingArea';
import { useAddStationeryMutation, useUpdateStationeryMutation, type AdminStationery } from './api';

/** Paper images are full sheets; the API takes up to 4 MB. */
const MAX_PAPER_BYTES = 4 * 1024 * 1024;

/** A paper being fitted: a new upload, or an existing paper being adjusted. */
interface PaperWork {
  imageUrl: string;
  width: number;
  height: number;
  initial: PaperDraft;
  /** New upload: the bytes to send. */
  blob?: Blob;
  /** Existing paper: its id. */
  id?: string;
}

/** Stamps per request: a cut stamp is ~50–150 KB; the API takes 8 MB a body. */
const BATCH = 6;

interface Pending {
  key: string;
  blob: Blob;
  url: string;
  name: string;
  keep: boolean;
  width: number;
  height: number;
  /** The cutter could not find a clean edge — worth a second look. */
  rough: boolean;
}

/**
 * A plain reason for a failed upload: "too large" when the server (or the
 * proxy in front of it) refused the size, the server's own message when it
 * gave one, and the fallback otherwise.
 */
export function uploadProblem(e: unknown, fallback: string): string {
  const err = e as { status?: unknown; data?: { message?: unknown } } | undefined;
  if (err?.status === 413) return 'That image is too large to upload. Try a smaller or more compressed file (under 4 MB).';
  const msg = err?.data?.message;
  if (typeof msg === 'string' && msg) return `${fallback} ${msg.charAt(0).toUpperCase()}${msg.slice(1)}.`;
  return fallback;
}

async function fileToPixels(file: File): Promise<Pixels> {
  const bmp = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(bmp, 0, 0);
  bmp.close();
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return { data: img.data, width: img.width, height: img.height };
}

/** WebP where the browser can encode it (transparent, small), PNG otherwise. */
async function pixelsToBlob(p: Pixels): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = p.width;
  canvas.height = p.height;
  canvas.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(p.data), p.width, p.height), 0, 0);
  const encode = (type: string, q?: number) =>
    new Promise<Blob | null>((res) => canvas.toBlob((b) => res(b), type, q));
  const webp = await encode('image/webp', 0.92);
  if (webp && webp.type === 'image/webp') return webp;
  return (await encode('image/png'))!;
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(bin);
}

async function sizeOf(blob: Blob): Promise<{ width: number; height: number }> {
  const bmp = await createImageBitmap(blob);
  const out = { width: bmp.width, height: bmp.height };
  bmp.close();
  return out;
}

export function AdminStamps({ members }: { members: AdminUserRow[] }) {
  const { data, isLoading, error } = useAdminStampsQuery();
  const [create, { isLoading: creating }] = useCreateStampCollectionMutation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [plusOnly, setPlusOnly] = useState(true);
  const [createError, setCreateError] = useState<string | null>(null);

  const onCreate = async () => {
    setCreateError(null);
    try {
      await create({ name: name.trim(), description: description.trim(), plusOnly }).unwrap();
      setName('');
      setDescription('');
    } catch {
      setCreateError('Could not create that stamp book.');
    }
  };

  if (isLoading) return <p className="admin__empty">Loading stamp books…</p>;
  if (error || !data) return <p className="admin__empty">Couldn’t load the stamp books. Try refreshing.</p>;

  return (
    <div className="admin-settings admin-stamps">
      <section className="admin-settings__group">
        <h2 className="admin-settings__title">New stamp book</h2>
        <p className="admin-setting__help">
          A set of designs members print their stamps with. Designs are artwork only — they never change postage or
          speed, and 2nd class stays free.
        </p>
        <div className="admin-stamps__new">
          <label className="admin-setting__input">
            <span>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Basquiat" maxLength={80} />
          </label>
          <label className="admin-setting__input admin-stamps__grow">
            <span>Description</span>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional, one line"
              maxLength={400}
            />
          </label>
          <label className="admin-setting__input">
            <input type="checkbox" checked={plusOnly} onChange={(e) => setPlusOnly(e.target.checked)} />
            <span>Plus only</span>
          </label>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!name.trim() || creating}
            onClick={() => void onCreate()}
          >
            Create
          </button>
        </div>
        {createError && <p className="admin-settings__error">{createError}</p>}
      </section>

      {data.collections.length === 0 && (
        <p className="admin__empty">No stamp books yet. Create one, then upload a sheet or single stamps into it.</p>
      )}
      {data.collections.map((c) => (
        <CollectionCard key={c.id} collection={c} />
      ))}

      <Grants members={members} collections={data.collections} grants={data.grants} />
    </div>
  );
}

function CollectionCard({ collection: c }: { collection: AdminStampCollection }) {
  const [update] = useUpdateStampCollectionMutation();
  const [updateDesign] = useUpdateStampDesignMutation();
  const [name, setName] = useState(c.name);
  const [description, setDescription] = useState(c.description);
  const [pending, setPending] = useState<Pending[] | null>(null);
  const [working, setWorking] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const singles = useRef<HTMLInputElement>(null);
  const sheets = useRef<HTMLInputElement>(null);
  const papersInput = useRef<HTMLInputElement>(null);
  const [paperWork, setPaperWork] = useState<PaperWork | null>(null);
  const envelopesInput = useRef<HTMLInputElement>(null);
  const [envWork, setEnvWork] = useState<{
    imageUrl: string;
    width: number;
    height: number;
    initial: EnvelopeDraft;
    blob?: Blob;
    id?: string;
  } | null>(null);
  const [addPaper, { isLoading: addingPaper }] = useAddStationeryMutation();
  const [updatePaper, { isLoading: updatingPaper }] = useUpdateStationeryMutation();
  const live = c.designs.filter((d) => d.active).length;
  const dirty = name.trim() !== c.name || description.trim() !== c.description;

  useEffect(() => {
    setName(c.name);
    setDescription(c.description);
  }, [c.name, c.description]);

  const nextNumber = c.designs.length + 1;

  const fromSingles = async (files: FileList) => {
    setProblem(null);
    setWorking('Reading stamps…');
    try {
      const out: Pending[] = [];
      for (const [i, file] of [...files].entries()) {
        const size = await sizeOf(file);
        out.push({
          key: `${file.name}-${i}`,
          blob: file,
          url: URL.createObjectURL(file),
          name: file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').slice(0, 80),
          keep: true,
          rough: false,
          ...size,
        });
      }
      setPending(out);
    } catch {
      setProblem('One of those files isn’t an image this browser can read.');
    } finally {
      setWorking(null);
    }
  };

  const fromSheets = async (files: FileList) => {
    setProblem(null);
    setWorking('Cutting the sheet…');
    try {
      const out: Pending[] = [];
      for (const file of files) {
        const cuts = cutSheet(await fileToPixels(file));
        for (const cut of cuts) {
          const blob = await pixelsToBlob(cut.pixels);
          out.push({
            key: `${file.name}-${cut.row}-${cut.col}`,
            blob,
            url: URL.createObjectURL(blob),
            name: `${c.name} ${nextNumber + out.length}`,
            keep: !cut.rough,
            rough: cut.rough,
            width: cut.pixels.width,
            height: cut.pixels.height,
          });
        }
      }
      if (out.length === 0) setProblem('No stamps found on that sheet. Try uploading them one by one instead.');
      else setPending(out);
    } catch {
      setProblem('That sheet couldn’t be read.');
    } finally {
      setWorking(null);
    }
  };

  const fromPaper = async (file: File) => {
    setProblem(null);
    setWorking('Finding the writing area…');
    try {
      const pixels = await fileToPixels(file);
      let blob: Blob = file;
      if (file.size > MAX_PAPER_BYTES) blob = await pixelsToBlob(pixels);
      setPaperWork({
        imageUrl: URL.createObjectURL(blob),
        width: pixels.width,
        height: pixels.height,
        blob,
        initial: {
          name: file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').slice(0, 80),
          area: detectWritingArea(pixels),
          ink: '#2C2420',
        },
      });
    } catch {
      setProblem('That paper image couldn’t be read.');
    } finally {
      setWorking(null);
    }
  };

  const savePaper = async (draft: PaperDraft) => {
    if (!paperWork) return;
    setProblem(null);
    try {
      if (paperWork.id) {
        await updatePaper({ id: paperWork.id, ...draft }).unwrap();
      } else if (paperWork.blob) {
        await addPaper({
          collectionId: c.id,
          paper: {
            ...draft,
            data: await blobToBase64(paperWork.blob),
            mime: paperWork.blob.type || 'image/png',
            width: paperWork.width,
            height: paperWork.height,
          },
        }).unwrap();
        URL.revokeObjectURL(paperWork.imageUrl);
      }
      setPaperWork(null);
    } catch (e) {
      setProblem(uploadProblem(e, 'That paper couldn’t be saved.'));
    }
  };

  const fromEnvelope = async (file: File) => {
    setProblem(null);
    setWorking('Reading the pattern…');
    try {
      const pixels = await fileToPixels(file);
      const blob: Blob = file.size > MAX_PAPER_BYTES ? await pixelsToBlob(pixels) : file;
      setEnvWork({
        imageUrl: URL.createObjectURL(blob),
        width: pixels.width,
        height: pixels.height,
        blob,
        initial: { name: file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').slice(0, 80), scale: 1 },
      });
    } catch {
      setProblem('That envelope pattern couldn’t be read.');
    } finally {
      setWorking(null);
    }
  };

  const saveEnvelope = async (draft: EnvelopeDraft) => {
    if (!envWork) return;
    setProblem(null);
    try {
      if (envWork.id) {
        await updatePaper({ id: envWork.id, ...draft }).unwrap();
      } else if (envWork.blob) {
        await addPaper({
          collectionId: c.id,
          paper: {
            kind: 'envelope',
            ...draft,
            data: await blobToBase64(envWork.blob),
            mime: envWork.blob.type || 'image/png',
            width: envWork.width,
            height: envWork.height,
          },
        }).unwrap();
        URL.revokeObjectURL(envWork.imageUrl);
      }
      setEnvWork(null);
    } catch (e) {
      setProblem(uploadProblem(e, 'That envelope couldn’t be saved.'));
    }
  };

  const adjust = (p: AdminStationery) =>
    setPaperWork({
      id: p.id,
      imageUrl: p.imageUrl,
      width: p.width,
      height: p.height,
      initial: { name: p.name, area: p.area, ink: p.ink },
    });

  return (
    <section className="admin-settings__group admin-stamps__book" aria-label={`Stamp book ${c.name}`}>
      <div className="admin-stamps__head">
        <h2 className="admin-settings__title">{c.name}</h2>
        <span className="admin-setting__help">
          {live} design{live === 1 ? '' : 's'} · {c.owners} member{c.owners === 1 ? '' : 's'} given it
          {c.active ? '' : ' · retired'}
        </span>
      </div>

      <div className="admin-stamps__new">
        <label className="admin-setting__input">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
        </label>
        <label className="admin-setting__input admin-stamps__grow">
          <span>Description</span>
          <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={400} />
        </label>
        {dirty && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={!name.trim()}
            onClick={() => void update({ id: c.id, name: name.trim(), description: description.trim() })}
          >
            Save
          </button>
        )}
      </div>
      <div className="admin-stamps__new">
        <label className="admin-setting__input" title="Plus members see it; others see a locked preview unless given it">
          <input type="checkbox" checked={c.plusOnly} onChange={(e) => void update({ id: c.id, plusOnly: e.target.checked })} />
          <span>Plus only</span>
        </label>
        <label className="admin-setting__input" title="A retired book is hidden from everyone; letters keep their stamps">
          <input type="checkbox" checked={c.active} onChange={(e) => void update({ id: c.id, active: e.target.checked })} />
          <span>Active</span>
        </label>
      </div>

      {c.designs.length > 0 && (
        <ol className="admin-stamps__grid">
          {c.designs.map((d) => (
            <li key={d.id} className={`admin-stamps__tile${d.active ? '' : ' is-retired'}`}>
              <PostageStamp cls="second" size="large" design={d} />
              <input
                className="admin-stamps__name"
                defaultValue={d.name}
                aria-label="Design name"
                maxLength={80}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && v !== d.name) void updateDesign({ id: d.id, name: v });
                }}
              />
              <button type="button" className="linkish" onClick={() => void updateDesign({ id: d.id, active: !d.active })}>
                {d.active ? 'Retire' : 'Restore'}
              </button>
            </li>
          ))}
        </ol>
      )}

      {c.papers.length > 0 && (
        <>
          <p className="admin-setting__label admin-stamps__sub">Paper</p>
          <ol className="admin-stamps__grid admin-stamps__grid--papers">
            {c.papers.map((p) => (
              <li key={p.id} className={`admin-stamps__tile${p.active ? '' : ' is-retired'}`}>
                <span
                  className="admin-stamps__paper"
                  style={{ backgroundImage: `url(${p.imageUrl})`, aspectRatio: `${p.width} / ${p.height}` }}
                  aria-hidden="true"
                />
                <span className="admin-stamps__dims">{p.name}</span>
                <span className="admin-stamps__paper-actions">
                  <button type="button" className="linkish" onClick={() => adjust(p)}>
                    Adjust
                  </button>
                  <button type="button" className="linkish" onClick={() => void updatePaper({ id: p.id, active: !p.active })}>
                    {p.active ? 'Retire' : 'Restore'}
                  </button>
                </span>
              </li>
            ))}
          </ol>
        </>
      )}

      {c.envelopes.length > 0 && (
        <>
          <p className="admin-setting__label admin-stamps__sub">Envelopes</p>
          <ol className="admin-stamps__grid admin-stamps__grid--papers">
            {c.envelopes.map((e) => (
              <li key={e.id} className={`admin-stamps__tile${e.active ? '' : ' is-retired'}`}>
                <EnvelopeThumb envelope={e} title={e.name} />
                <span className="admin-stamps__dims">{e.name}</span>
                <span className="admin-stamps__paper-actions">
                  <button
                    type="button"
                    className="linkish"
                    onClick={() =>
                      setEnvWork({ id: e.id, imageUrl: e.imageUrl, width: e.width, height: e.height, initial: { name: e.name, scale: e.scale } })
                    }
                  >
                    Adjust
                  </button>
                  <button type="button" className="linkish" onClick={() => void updatePaper({ id: e.id, active: !e.active })}>
                    {e.active ? 'Retire' : 'Restore'}
                  </button>
                </span>
              </li>
            ))}
          </ol>
        </>
      )}

      {envWork && (
        <EnvelopeEditor
          imageUrl={envWork.imageUrl}
          width={envWork.width}
          height={envWork.height}
          initial={envWork.initial}
          saving={addingPaper || updatingPaper}
          saveLabel={envWork.id ? 'Save changes' : 'Save envelope'}
          onSave={(d) => void saveEnvelope(d)}
          onCancel={() => {
            if (envWork.blob) URL.revokeObjectURL(envWork.imageUrl);
            setEnvWork(null);
          }}
        />
      )}

      {paperWork && (
        <PaperEditor
          imageUrl={paperWork.imageUrl}
          width={paperWork.width}
          height={paperWork.height}
          initial={paperWork.initial}
          saving={addingPaper || updatingPaper}
          saveLabel={paperWork.id ? 'Save changes' : 'Save paper'}
          onSave={(d) => void savePaper(d)}
          onCancel={() => {
            if (paperWork.blob) URL.revokeObjectURL(paperWork.imageUrl);
            setPaperWork(null);
          }}
        />
      )}

      {pending ? (
        <Review
          collection={c}
          items={pending}
          onChange={setPending}
          onDone={() => {
            pending.forEach((p) => URL.revokeObjectURL(p.url));
            setPending(null);
          }}
        />
      ) : (
        <div className="admin-stamps__actions">
          <button type="button" className="btn btn--ghost" disabled={!!working} onClick={() => sheets.current?.click()}>
            <Scissors size={14} aria-hidden="true" /> Cut a sheet
          </button>
          <button type="button" className="btn btn--ghost" disabled={!!working} onClick={() => singles.current?.click()}>
            <Upload size={14} aria-hidden="true" /> Upload single stamps
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={!!working || !!paperWork}
            onClick={() => papersInput.current?.click()}
          >
            <FileText size={14} aria-hidden="true" /> Add letter paper
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={!!working || !!envWork}
            onClick={() => envelopesInput.current?.click()}
          >
            <Mail size={14} aria-hidden="true" /> Add envelope
          </button>
          <input
            ref={envelopesInput}
            type="file"
            accept="image/png,image/webp,image/jpeg"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void fromEnvelope(f);
              e.target.value = '';
            }}
          />
          <input
            ref={papersInput}
            type="file"
            accept="image/png,image/webp,image/jpeg"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void fromPaper(f);
              e.target.value = '';
            }}
          />
          <span className="admin-setting__help">
            {working ??
              'A sheet: one image of stamps in a grid on a plain ground — each is cut out with its perforated edge. Single stamps are kept exactly as uploaded (PNG or WebP with a transparent background works best).'}
          </span>
          <input
            ref={sheets}
            type="file"
            accept="image/png,image/webp,image/jpeg"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files?.length) void fromSheets(e.target.files);
              e.target.value = '';
            }}
          />
          <input
            ref={singles}
            type="file"
            accept="image/png,image/webp,image/jpeg"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files?.length) void fromSingles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      )}
      {problem && <p className="admin-settings__error">{problem}</p>}
    </section>
  );
}

/** The cut (or uploaded) stamps, before anything is saved: untick, rename, save. */
function Review({
  collection,
  items,
  onChange,
  onDone,
}: {
  collection: AdminStampCollection;
  items: Pending[];
  onChange: (items: Pending[]) => void;
  onDone: () => void;
}) {
  const [add] = useAddStampDesignsMutation();
  const [saving, setSaving] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const kept = items.filter((i) => i.keep);
  const patch = (key: string, p: Partial<Pending>) => onChange(items.map((i) => (i.key === key ? { ...i, ...p } : i)));

  const save = async () => {
    setProblem(null);
    try {
      for (let i = 0; i < kept.length; i += BATCH) {
        setSaving(`Saving ${Math.min(i + BATCH, kept.length)} of ${kept.length}…`);
        const batch = await Promise.all(
          kept.slice(i, i + BATCH).map(async (k) => ({
            name: k.name.trim() || 'Stamp',
            data: await blobToBase64(k.blob),
            mime: k.blob.type || 'image/png',
            width: k.width,
            height: k.height,
          })),
        );
        await add({ collectionId: collection.id, designs: batch }).unwrap();
        // Saved ones leave the review, so a failure part-way can be retried.
        const savedKeys = new Set(kept.slice(i, i + BATCH).map((k) => k.key));
        items = items.filter((it) => !savedKeys.has(it.key));
        onChange(items);
      }
      onDone();
    } catch (e) {
      setProblem(uploadProblem(e, 'Some stamps couldn’t be saved. The ones still here can be tried again.'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="admin-stamps__review">
      <p className="admin-setting__label">
        Review: {kept.length} of {items.length} selected
      </p>
      <p className="admin-setting__help">Untick any bad cuts and name the rest. Nothing is saved until you press Save.</p>
      <ol className="admin-stamps__grid">
        {items.map((it) => (
          <li key={it.key} className={`admin-stamps__tile admin-stamps__cut${it.keep ? '' : ' is-off'}${it.rough ? ' is-rough' : ''}`}>
            <label className="admin-stamps__pick">
              <input type="checkbox" checked={it.keep} onChange={(e) => patch(it.key, { keep: e.target.checked })} />
              <span className="admin-stamps__preview">
                <img src={it.url} alt="" />
              </span>
            </label>
            <input
              className="admin-stamps__name"
              value={it.name}
              aria-label="Stamp name"
              maxLength={80}
              onChange={(e) => patch(it.key, { name: e.target.value })}
            />
            <span className="admin-stamps__dims">
              {it.width}×{it.height}
              {it.rough ? ' · rough cut' : ''}
            </span>
          </li>
        ))}
      </ol>
      <div className="admin-stamps__actions">
        <button type="button" className="btn btn--primary" disabled={kept.length === 0 || !!saving} onClick={() => void save()}>
          {saving ?? `Save ${kept.length} stamp${kept.length === 1 ? '' : 's'}`}
        </button>
        <button type="button" className="btn btn--ghost" disabled={!!saving} onClick={onDone}>
          Discard
        </button>
      </div>
      {problem && <p className="admin-settings__error">{problem}</p>}
    </div>
  );
}

function Grants({
  members,
  collections,
  grants,
}: {
  members: AdminUserRow[];
  collections: AdminStampCollection[];
  grants: { userId: string; email: string; handle: string | null; collectionId: string; collectionName: string; createdAt: string }[];
}) {
  const [grant, { isLoading }] = useGrantStampCollectionMutation();
  const [revoke] = useRevokeStampGrantMutation();
  const [who, setWho] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const match = useMemo(() => {
    const q = who.trim().replace(/^@/, '').toLowerCase();
    if (!q) return null;
    return members.find((m) => m.email.toLowerCase() === q || (m.handle ?? '').toLowerCase() === q) ?? null;
  }, [who, members]);

  const onGrant = async () => {
    if (!match || !collectionId) return;
    setProblem(null);
    try {
      await grant({ userId: match.id, collectionId }).unwrap();
      setWho('');
    } catch {
      setProblem('Could not grant that one.');
    }
  };

  return (
    <section className="admin-settings__group">
      <h2 className="admin-settings__title">Give a stamp book to a member</h2>
      <p className="admin-setting__help">
        Unlocks a book for one member straight away, Plus or not. Not counted against anyone’s monthly gift.
      </p>
      <div className="admin-stamps__new">
        <label className="admin-setting__input admin-stamps__grow">
          <span>Member</span>
          <input
            list="admin-stamps-members"
            value={who}
            onChange={(e) => setWho(e.target.value)}
            placeholder="email or @handle"
          />
          <datalist id="admin-stamps-members">
            {members.map((m) => (
              <option key={m.id} value={m.email}>
                {m.handle ? `@${m.handle}` : m.name}
              </option>
            ))}
          </datalist>
        </label>
        <label className="admin-setting__input">
          <span>Book</span>
          <select value={collectionId} onChange={(e) => setCollectionId(e.target.value)}>
            <option value="">Choose…</option>
            {collections.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn btn--primary" disabled={!match || !collectionId || isLoading} onClick={() => void onGrant()}>
          Give
        </button>
      </div>
      {who.trim() && !match && <p className="admin-setting__help">No member with that email or handle.</p>}
      {problem && <p className="admin-settings__error">{problem}</p>}
      {grants.length > 0 && (
        <ul className="admin-stamps__grants">
          {grants.map((g) => (
            <li key={`${g.userId}-${g.collectionId}`}>
              <span>
                {g.collectionName} → {g.handle ? `@${g.handle}` : g.email}
              </span>
              <time dateTime={g.createdAt}>{new Date(g.createdAt).toLocaleDateString([], { day: 'numeric', month: 'short' })}</time>
              <button type="button" className="linkish" onClick={() => void revoke({ userId: g.userId, collectionId: g.collectionId })}>
                Take back
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
